'use client'

/**
 * GridArchiveFeatured - Featured Items Component
 * ===============================================
 *
 * This component runs inside a separate <Index indexId="featured"> context,
 * which gives it an isolated search state from the main grid query.
 *
 * KEY RESPONSIBILITIES:
 * 1. Render the 6 large featured items on page 1
 * 2. Collect and report the objectIDs of featured items via onFeaturedIds
 *    so the main query can exclude them (prevents duplicates)
 *
 * WHY SEPARATE INDEX CONTEXT:
 * By using indexId="featured", this component has its own:
 * - Pagination (always page 0)
 * - hitsPerPage (always 6)
 * - Results set
 *
 * This means when a user navigates to page 2, the featured query stays on
 * page 0 and keeps returning the same 6 items - which we use to maintain
 * consistent exclusion filters for the main query.
 *
 * VISIBILITY LOGIC:
 * - Only renders on page 1 when not filtered
 * - Always collects IDs (even when not rendering) so main query can exclude them
 */

import { useEffect } from 'react'
import { useHits, useInstantSearch } from 'react-instantsearch'

import { checkIsFiltered } from '@/lib/utils/algolia'
import GridArchiveItem from '@/components/grid-archive/GridArchiveItem'

import type { Hit } from 'instantsearch.js'

type GridArchiveFeaturedProps = {
  onFeaturedIds: (ids: string[]) => void
  onFeaturedRendered: (rendered: boolean) => void
  sidebarContent?: React.ReactNode
  isFirstPage: boolean
}

export default function GridArchiveFeatured({
  onFeaturedIds,
  onFeaturedRendered,
  sidebarContent,
  isFirstPage,
}: GridArchiveFeaturedProps): JSX.Element | null {
  const { items } = useHits()
  const { status } = useInstantSearch()
  const isFiltered = checkIsFiltered({ includePage: false })

  const shouldRender = isFirstPage && !isFiltered && items?.length > 0
  const shouldCollectIds = !isFiltered && items?.length > 0

  useEffect(() => {
    // Don't exclude any posts if a filter is applied; only exclude on the default unfiltered view
    if (!shouldCollectIds) {
      onFeaturedIds([])
      onFeaturedRendered(true)
      return
    }

    // Only update when we have results and the search is idle
    if (status === 'idle' && items?.length > 0) {
      const ids = items.map((item) => item.objectID)
      onFeaturedIds(ids)
      onFeaturedRendered(true)
    }
  }, [items, status, onFeaturedIds, shouldCollectIds, onFeaturedRendered])

  // Only render featured items on page 1 when not filtered
  if (!shouldRender) {
    return null
  }

  const firstGridRowClass = sidebarContent
    ? 'm-up:w-[calc(100%-400px)] m:w-full grid-cols-3 m:grid-cols-2'
    : 'col-span-4 grid-cols-5 xl:grid-cols-4 m:grid-cols-2'

  return (
    <div className="container mb-l">
      <div className="flex items-start gap-l s:flex-col">
        <div
          className={`grid ${firstGridRowClass} w-full gap-l s:gap-x-s`}
          style={{ viewTransitionName: 'featured-grid-transition' }}>
          {items.map((item: Hit, i) => (
            <GridArchiveItem
              key={item.objectID}
              hit={item}
              size="l"
              className={i < 2 ? 's:col-span-2' : 's:col-span-1'}
            />
          ))}
        </div>

        {sidebarContent ? <div className="m:hidden">{sidebarContent}</div> : null}
      </div>
    </div>
  )
}
